import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserCheck, UserX, Repeat2, Languages, UserCog } from "lucide-react";
import { StatCard, BarRow } from "./DashBits";
import WidgetExport from "./WidgetExport";
import ReassignDialog from "@/components/leads/ReassignDialog";

export default function AssignmentTab({ data, drill, onReload }) {
  const navigate = useNavigate();
  const a = data?.assignment || {};
  const byAssignee = a.by_assignee || [];
  const history = a.recent_history || [];
  const pending = a.arabic_pending || [];
  const totalAssigned = a.assigned || 0;
  const [reassignIds, setReassignIds] = useState(null);
  return (
    <div className="space-y-4" data-testid="dash-tab-assignment">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Assigned" value={a.assigned ?? 0} icon={UserCheck} accent="text-emerald-600"
                  testid="kpi-asn-assigned" />
        <StatCard label="Unassigned" value={a.unassigned ?? 0} icon={UserX} accent="text-rose-600"
                  onClick={() => drill({ assignee: "__unassigned__" })} testid="kpi-asn-unassigned" />
        <StatCard label="Reassigned Leads" value={a.reassigned ?? 0} icon={Repeat2} accent="text-purple-600"
                  sub="moved between agents at least once" testid="kpi-asn-reassigned" />
        <StatCard label="Arabic — Pending" value={a.arabic_pending_count ?? 0} icon={Languages} accent="text-amber-600"
                  sub="waiting for an Arabic agent" testid="kpi-asn-arabic-pending" />
      </div>

      {pending.length > 0 && (
        <Card className="border-amber-200" data-testid="arabic-pending-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
              <Languages className="w-4 h-4" /> Arabic — Pending Assignment · {pending.length}
            </CardTitle>
            <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700"
                    onClick={() => setReassignIds(pending.map((p) => p.id))}
                    data-testid="arabic-pending-assign-all">
              <UserCog className="w-3.5 h-3.5 mr-1.5" /> Assign all
            </Button>
          </CardHeader>
          <CardContent className="p-2">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-slate-400">
                <tr><th className="text-left px-2 py-1">Lead</th><th className="text-left px-2">Class</th>
                    <th className="text-left px-2">Waiting since</th><th className="px-2 w-24"></th></tr>
              </thead>
              <tbody>
                {pending.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-amber-50/40">
                    <td className="px-2 py-1.5 cursor-pointer" onClick={() => navigate(`/leads/${p.id}`)}>
                      <div className="text-slate-800 font-medium">{p.customer_name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{p.customer_id}</div>
                    </td>
                    <td className="px-2 font-mono text-[11px]">{p.classification ?? "—"}</td>
                    <td className="px-2 font-mono text-[10px] text-slate-500">
                      {(p.updated_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-2 text-right">
                      <Button variant="outline" size="sm" className="h-6 px-2 text-[10px]"
                              onClick={() => setReassignIds([p.id])}
                              data-testid={`arabic-pending-assign-${p.customer_id}`}>
                        <UserCog className="w-3 h-3 mr-1" /> Assign
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <ReassignDialog
        open={!!reassignIds}
        onOpenChange={(v) => { if (!v) setReassignIds(null); }}
        leadIds={reassignIds || []}
        arabicHint
        onDone={() => onReload?.()}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm">Leads by Assignee</CardTitle>
            <WidgetExport title="Leads by Assignee" headers={["Agent", "Email", "Leads"]}
                          getRows={() => byAssignee.map((r) => [r.name || r.assignee, r.assignee, r.count])} />
          </CardHeader>
          <CardContent className="space-y-1.5" data-testid="by-assignee-card">
            {byAssignee.length === 0 && <div className="text-xs text-slate-400">No leads assigned yet.</div>}
            {byAssignee.map((r) => (
              <BarRow key={r.assignee} label={r.name || r.assignee} count={r.count} total={totalAssigned}
                      color="#6366F1" onClick={() => drill({ assignee: r.assignee })}
                      testid={`assignee-bar-${r.assignee}`} />
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Recent Assignment History</CardTitle></CardHeader>
          <CardContent className="p-2" data-testid="assignment-history">
            {history.length === 0 ? (
              <div className="text-xs text-slate-400 p-2">No assignment events yet.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-slate-400">
                  <tr><th className="text-left px-2 py-1">When</th><th className="text-left px-2">Lead</th>
                      <th className="text-left px-2">To</th><th className="text-left px-2">By / Reason</th></tr>
                </thead>
                <tbody>
                  {history.map((ev, i) => (
                    <tr key={`${ev.lead_id}-${i}`} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-mono text-[10px] text-slate-500">
                        {(ev.at || "").slice(0, 16).replace("T", " ")}
                      </td>
                      <td className="px-2">
                        <div className="text-slate-800">{ev.customer_name}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{ev.customer_id}</div>
                      </td>
                      <td className="px-2 text-slate-700">{ev.assigned_to || "—"}</td>
                      <td className="px-2 text-slate-500">
                        {ev.by}{ev.reason ? ` · ${ev.reason}` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
